// @flow
import React from 'react'
import { styled } from 'styled-components'

import { CATEGORIES, type Category } from '../constants'
import type { PerformanceScores } from '@mission.io/mission-toolkit'
import { ColorCoin } from './sharedComponents'
import { translateScore, getBaseProficiency, SCORES } from './helpers'
import { cssPlayerHeightUnit } from '../../../../../utility'

/**
 * A Bar Graph that shows the distribution of a classes proficiency scores for each analytics metric category:
 * Questions, Application, Collaboration, CriticalThinking, Resilience, and Initiative.
 * @param {{ [string]: SelScores }} props.analytics - The analytics object containing all the students scores for the mission.
 * @returns
 */
export default function ProficiencyBarGraph({
	scores,
}: {
	scores: PerformanceScores,
}): React$Node {
	return (
		<Grid>
			<div className="legend-style">
				<ProficiencyLegend className="bottom-space" />
			</div>

			<MedallionAverages categories={CATEGORIES} average={scores} />
		</Grid>
	)
}

function ProficiencyLegend(props: { className?: string }): React$Node {
	return (
		<div {...props}>
			{SCORES.map(({ level, description }) => (
				<ListItem key={level}>
					<ColorCoin level={level} /> {level} - {description}
				</ListItem>
			))}
		</div>
	)
}

/**
 * A component that displays a list of students average scores.
 * @param {Category[]} categories categories student will be graded
 * @param {?SelScores} average average sel scores earned by a student.
 * @returns {React$Node}
 */
function MedallionAverages({
	categories,
	average,
	...props
}: {
	categories: Category[],
	average: ?PerformanceScores,
}): React$Node {
	return (
		<Container {...props}>
			{categories.map(category => {
				const proficiencyLevel = getBaseProficiency(
					translateScore(average ? category.lookup(average) : null)
				)
				return (
					<div key={category.id} className="category">
						<div className="coin-wrapper">
							<ColorCoin level={proficiencyLevel}>
								<span>{proficiencyLevel || 'N/A'}</span>
							</ColorCoin>
						</div>
						<div className="title">{category.title}</div>
					</div>
				)
			})}
		</Container>
	)
}

const Container = styled.div`
	display: flex;
	flex: 1;
	margin-left: ${cssPlayerHeightUnit(2)};

	.category {
		.title {
			margin-top: ${cssPlayerHeightUnit(2)};
		}
		flex: 1 1 0;
		width: 0;
		text-align: center;
		.coin-wrapper {
			padding: 0 ${cssPlayerHeightUnit(2)};
		}
		${ColorCoin} {
			width: 100%;
			display: flex;
			justify-content: center;
			align-items: center;
			font-size: ${cssPlayerHeightUnit(8)};
		}
	}
`

const Grid = styled.div`
	font-size: 2em;
	padding: ${({ theme }) => `${theme.spacing2x} ${theme.spacing4x}`};
	display: flex;
	align-items: center;

	.legend-style {
		background-color: #3f3b6e;
		padding: ${cssPlayerHeightUnit(3)};
		border-radius: ${cssPlayerHeightUnit(2)};
	}
`

const ListItem = styled.div`
	display: flex;
	align-items: center;
	&:not(:last-child) {
		margin-bottom: var(--spacing);
	}
`
